/* Lumetier export — renders the same intro animation to a 2D canvas so we can ship it
   as PNG (static) or WebM (animated) without any external libraries.
   The animation timings + easings here mirror what `LumetierLogo.jsx` does in CSS so
   the live preview and the downloaded files stay visually identical. */

// Easing — matches the CSS `cubic-bezier(0.16, 1, 0.3, 1)` used throughout the logo.
// Source: re-implemented in plain JS so we don't pull in a bezier-eval library.
function bezier(t, p1x, p1y, p2x, p2y) {
  // Standard 1D cubic bezier via Newton-Raphson on the X axis.
  const cx = 3 * p1x, bx = 3 * (p2x - p1x) - cx, ax = 1 - cx - bx;
  const cy = 3 * p1y, by = 3 * (p2y - p1y) - cy, ay = 1 - cy - by;
  let x = t;
  for (let i = 0; i < 8; i++) {
    const xN = ((ax * x + bx) * x + cx) * x - t;
    const dN = (3 * ax * x + 2 * bx) * x + cx;
    if (Math.abs(dN) < 1e-6) break;
    x -= xN / dN;
  }
  return ((ay * x + by) * x + cy) * x;
}
const easeOutExpo = (t) => bezier(Math.max(0, Math.min(1, t)), 0.16, 1, 0.3, 1);
const clamp01 = (t) => Math.max(0, Math.min(1, t));

const BLADES = 6;
const BLADE_DELAY = 0.08;       // seconds between each blade entrance
const BLADE_DUR = 1.2;          // each blade fully reveals in 1.2s
const DOT_START = 1.3;
const DOT_DUR = 1.1;
const WORDMARK_START = 0.8;
const LETTER_STAGGER = 0.07;
const LETTER_DUR = 0.7;
const RULE_START = 1.6;
const RULE_DUR = 0.8;
const TAGLINE_START = 1.9;
const TAGLINE_DUR = 0.8;
const INTRO_END = 2.6;          // when the breathe loop takes over

// Single "draw the logo at time T" function — used by both the static PNG export
// (called once at T = 3.0s for the fully-revealed pose) and the WebM recorder
// (called every animation frame).
export function drawLumetierFrame(ctx, t, opts = {}) {
  const {
    size = 1080,
    bg = "#fafaf9",
    fg = "#1c1917",
    accent = "#C9A961",
    sub = "rgba(28, 25, 23, 0.55)",
  } = opts;
  ctx.save();
  // Background
  if (bg === "transparent") {
    ctx.clearRect(0, 0, size, size);
  } else {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, size, size);
  }

  // Layout — the aperture sits roughly in the top half, wordmark + rule + tagline below.
  const cx = size / 2;
  const apertureY = size * 0.36;
  const apertureR = size * 0.18;          // outer radius of each blade
  const apertureInner = size * 0.005;     // tiny inner cluster (closed pose)

  // ---- Aperture blades ----
  // Each blade is a thin triangular wedge that rotates+scales from a closed pose.
  for (let i = 0; i < BLADES; i++) {
    const localT = (t - i * BLADE_DELAY) / BLADE_DUR;
    const introProgress = easeOutExpo(clamp01(localT));
    // After the intro, all blades breathe in unison on a 6s loop (1 → 1.03 → 1).
    const breatheT = Math.max(0, (t - INTRO_END) / 6);
    const breathe = 1 + 0.03 * Math.sin(breatheT * Math.PI * 2 * 0.5);
    const scale = (0.15 + (1 - 0.15) * introProgress) * (t > INTRO_END ? breathe : 1);
    const alpha = clamp01(introProgress * 1.6);

    const angle = (Math.PI * 2 / BLADES) * i;
    ctx.save();
    ctx.translate(cx, apertureY);
    ctx.rotate(angle);
    ctx.scale(scale, scale);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = fg;
    // The wedge: tip at top, base near center. Coordinate space is normalized to the
    // aperture radius so blades scale uniformly regardless of canvas size.
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, -apertureR);
    ctx.lineTo(apertureR * 0.36, -apertureR * 0.58);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  // Small inner cluster always drawn at full opacity to suggest the closed-iris core.
  // (Visually negligible once blades open — but keeps the silhouette tidy.)
  ctx.fillStyle = fg;
  ctx.beginPath();
  ctx.arc(cx, apertureY, apertureInner, 0, Math.PI * 2);
  ctx.fill();

  // ---- Center accent dot ----
  const dotLocal = (t - DOT_START) / DOT_DUR;
  if (dotLocal > 0) {
    const dp = clamp01(dotLocal);
    // Slight overshoot (1 → 1.4 → 1) matching the CSS keyframe.
    const dotScale = dp < 0.6 ? (dp / 0.6) * 1.4 : 1.4 - ((dp - 0.6) / 0.4) * 0.4;
    ctx.save();
    ctx.translate(cx, apertureY);
    ctx.scale(dotScale, dotScale);
    ctx.globalAlpha = clamp01(dp * 2);
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.arc(0, 0, size * 0.018, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // ---- Wordmark "LUMETIER" ----
  const wordY = size * 0.68;
  const letterSize = size * 0.055;
  const letters = "LUMETIER".split("");
  // Cormorant Garamond → fallback chain matches the live component. The font must be
  // loaded by the caller before invoking (see ensureFonts() below) or canvas will fall
  // back to a generic serif.
  const serifFont = "300 " + letterSize + "px 'Cormorant Garamond', 'EB Garamond', 'Playfair Display', Georgia, serif";
  ctx.font = serifFont;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  // letter-spacing emulation — measure each letter individually so we can place them
  // with extra spacing. Canvas 2D doesn't natively support letter-spacing (yet).
  const spacing = letterSize * 0.42;
  const widths = letters.map((ch) => ctx.measureText(ch).width);
  const total = widths.reduce((a, b) => a + b, 0) + spacing * (letters.length - 1);
  let x = cx - total / 2;
  letters.forEach((ch, i) => {
    const lp = clamp01((t - WORDMARK_START - i * LETTER_STAGGER) / LETTER_DUR);
    const e = easeOutExpo(lp);
    ctx.save();
    ctx.globalAlpha = e;
    ctx.translate(0, (1 - e) * size * 0.012);
    ctx.fillStyle = fg;
    ctx.fillText(ch, x + widths[i] / 2, wordY);
    ctx.restore();
    x += widths[i] + spacing;
  });

  // ---- Gold rule ----
  const rp = easeOutExpo(clamp01((t - RULE_START) / RULE_DUR));
  if (rp > 0) {
    const ruleY = size * 0.745;
    const ruleHalf = size * 0.05 * rp;
    ctx.fillStyle = accent;
    ctx.fillRect(cx - ruleHalf, ruleY, ruleHalf * 2, Math.max(1, size * 0.0015));
  }

  // ---- Tagline ----
  const gp = easeOutExpo(clamp01((t - TAGLINE_START) / TAGLINE_DUR));
  if (gp > 0) {
    ctx.save();
    ctx.globalAlpha = gp;
    ctx.translate(0, (1 - gp) * size * 0.01);
    ctx.font = "500 " + (size * 0.018) + "px ui-sans-serif, system-ui, -apple-system, sans-serif";
    ctx.fillStyle = sub;
    ctx.textAlign = "center";
    // letter-spacing emulation by drawing each char
    const tagline = "EVERYTHING BEHIND YOUR BEST WORK.";
    // Slightly wider tracking for the shorter tagline so it visually balances the wordmark above.
    const taglineSpace = size * 0.0075;
    const tags = tagline.split("");
    const tagWidths = tags.map((ch) => ctx.measureText(ch).width);
    const tagTotal = tagWidths.reduce((a, b) => a + b, 0) + taglineSpace * (tags.length - 1);
    let tx = cx - tagTotal / 2;
    tags.forEach((ch, i) => {
      ctx.fillText(ch, tx + tagWidths[i] / 2, size * 0.79);
      tx += tagWidths[i] + taglineSpace;
    });
    ctx.restore();
  }

  ctx.restore();
}

// Load Cormorant Garamond from Google Fonts at runtime so canvas text matches the DOM
// version. Returns once the font is actually parsed + ready — the FontFace API blocks
// until the bytes have downloaded and decoded.
export async function ensureFonts() {
  if (document.fonts.check("300 40px 'Cormorant Garamond'")) return;
  const link = document.querySelector('link[href*="Cormorant+Garamond"]');
  if (!link) {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400&display=swap";
    document.head.appendChild(l);
  }
  // Wait until the browser confirms the font is ready (max ~3s).
  for (let i = 0; i < 30; i++) {
    if (document.fonts.check("300 40px 'Cormorant Garamond'")) return;
    await new Promise((r) => setTimeout(r, 100));
  }
}

// ---- Public exports used by the preview page ----
export async function exportPNG({ size = 1080, bg = "#fafaf9", inverted = false } = {}) {
  await ensureFonts();
  const cv = document.createElement("canvas");
  cv.width = size; cv.height = size;
  const ctx = cv.getContext("2d");
  // Snapshot at t = 3.0s so the logo is in its fully-revealed pose (breathe just started).
  drawLumetierFrame(ctx, 3.0, inverted
    ? { size, bg: bg === "transparent" ? "transparent" : "#0c0a09", fg: "#fafaf9", accent: "#C9A961", sub: "rgba(250,250,249,0.55)" }
    : { size, bg, fg: "#1c1917", accent: "#C9A961", sub: "rgba(28,25,23,0.55)" });
  return new Promise((resolve) => {
    cv.toBlob((blob) => {
      triggerDownload(blob, `lumetier-logo-${size}px${inverted ? "-dark" : ""}${bg === "transparent" ? "-transparent" : ""}.png`);
      resolve();
    }, "image/png");
  });
}

export function exportSVG({ inverted = false } = {}) {
  // Static SVG — same fully-revealed pose, scalable to any size in design tools.
  const fg = inverted ? "#fafaf9" : "#1c1917";
  const bg = inverted ? "#0c0a09" : "#fafaf9";
  const sub = inverted ? "rgba(250,250,249,0.55)" : "rgba(28,25,23,0.55)";
  const accent = "#C9A961";
  const blades = Array.from({ length: BLADES }, (_, i) => {
    const angle = (360 / BLADES) * i;
    return `<polygon points="50,50 50,12 64,28" fill="${fg}" transform="rotate(${angle} 50 50)"/>`;
  }).join("");
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 140" width="500" height="700">
  <rect width="100" height="140" fill="${bg}"/>
  <g transform="translate(0 5)">${blades}<circle cx="50" cy="50" r="3.4" fill="${accent}"/></g>
  <text x="50" y="106" text-anchor="middle" fill="${fg}" font-family="'Cormorant Garamond', Georgia, serif" font-weight="300" font-size="8" letter-spacing="3">LUMETIER</text>
  <line x1="40" y1="112" x2="60" y2="112" stroke="${accent}" stroke-width="0.4"/>
  <text x="50" y="121" text-anchor="middle" fill="${sub}" font-family="ui-sans-serif, system-ui, sans-serif" font-weight="500" font-size="3" letter-spacing="0.55">EVERYTHING BEHIND YOUR BEST WORK.</text>
</svg>`;
  const blob = new Blob([svg], { type: "image/svg+xml" });
  triggerDownload(blob, `lumetier-logo${inverted ? "-dark" : ""}.svg`);
}

export async function exportWebM({ size = 1080, fps = 30, durationSec = 4.5, inverted = false, onProgress, download = true } = {}) {
  // Records the canvas via MediaRecorder. WebM uploads cleanly to IG / TikTok / X /
  // LinkedIn but most desktop video players (QuickTime, Win Media Player) can't decode
  // it — that's why we ALSO expose a GIF export and an in-page preview.
  // Returns { blob, url } so callers can preview before deciding to download.
  await ensureFonts();
  const cv = document.createElement("canvas");
  cv.width = size; cv.height = size;
  const ctx = cv.getContext("2d");
  const stream = cv.captureStream(fps);

  // Pick the best codec the browser supports (Chrome/Edge → vp9, Safari → vp8/h264)
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
    "video/mp4",
  ];
  const mimeType = candidates.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m));
  if (!mimeType) throw new Error("This browser doesn't support video recording. Try Chrome or Edge.");

  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 8_000_000 });
  const chunks = [];
  recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  const done = new Promise((resolve) => { recorder.onstop = resolve; });
  recorder.start();

  const opts = inverted
    ? { size, bg: "#0c0a09", fg: "#fafaf9", accent: "#C9A961", sub: "rgba(250,250,249,0.55)" }
    : { size, bg: "#fafaf9", fg: "#1c1917", accent: "#C9A961", sub: "rgba(28,25,23,0.55)" };

  const start = performance.now();
  await new Promise((resolve) => {
    function tick() {
      const elapsed = (performance.now() - start) / 1000;
      if (elapsed >= durationSec) { resolve(); return; }
      drawLumetierFrame(ctx, elapsed, opts);
      if (onProgress) onProgress(elapsed / durationSec);
      requestAnimationFrame(tick);
    }
    tick();
  });

  recorder.stop();
  await done;
  const ext = mimeType.startsWith("video/mp4") ? "mp4" : "webm";
  const blob = new Blob(chunks, { type: mimeType });
  const url = URL.createObjectURL(blob);
  if (download) {
    triggerDownload(blob, `lumetier-logo${inverted ? "-dark" : ""}.${ext}`);
  }
  return { blob, url, ext };
}

export async function exportGIF({ size = 600, fps = 20, durationSec = 4.5, inverted = false, onProgress } = {}) {
  // Animated GIF — universally compatible (iMessage, Twitter, Slack, every browser,
  // every social platform). Smaller resolution + lower fps because GIF is paletted
  // (256 colors) and would balloon in size at 1080p/30fps.
  await ensureFonts();
  // Lazy-load gifenc so the main bundle isn't bloated for the 99% of pages that don't
  // need GIF encoding.
  const { GIFEncoder, quantize, applyPalette } = await import("gifenc");
  const cv = document.createElement("canvas");
  cv.width = size; cv.height = size;
  const ctx = cv.getContext("2d");
  const opts = inverted
    ? { size, bg: "#0c0a09", fg: "#fafaf9", accent: "#C9A961", sub: "rgba(250,250,249,0.55)" }
    : { size, bg: "#fafaf9", fg: "#1c1917", accent: "#C9A961", sub: "rgba(28,25,23,0.55)" };

  const gif = GIFEncoder();
  const totalFrames = Math.round(durationSec * fps);
  const frameDelayMs = Math.round(1000 / fps);

  for (let f = 0; f < totalFrames; f++) {
    const t = f / fps;
    drawLumetierFrame(ctx, t, opts);
    const { data } = ctx.getImageData(0, 0, size, size);
    // Quantize to a 256-color palette per-frame. `format: "rgb444"` gives a great
    // size/quality tradeoff for a near-monochrome wordmark + gold accent.
    const palette = quantize(data, 256, { format: "rgb444" });
    const index = applyPalette(data, palette, "rgb444");
    gif.writeFrame(index, size, size, { palette, delay: frameDelayMs });
    if (onProgress) onProgress(f / totalFrames);
    // Yield to the browser every few frames so the UI stays responsive
    if (f % 6 === 0) await new Promise((r) => setTimeout(r, 0));
  }
  gif.finish();
  const blob = new Blob([gif.bytes()], { type: "image/gif" });
  triggerDownload(blob, `lumetier-logo${inverted ? "-dark" : ""}.gif`);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
